import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ShieldCheck, RefreshCw, UserRound, Building2, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { adminRevokeUserSessionsFn, adminSearchUsersFn } from "./-dashboard.admin.users.functions";

export const Route = createFileRoute("/dashboard/admin/users")({
  head: () => ({ meta: [{ title: "User management · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminUsersPage,
});

type AdminUser = {
  id: string;
  name: string;
  email: string;
  email_verified: boolean;
  created_at: string;
  roles: string[];
  provider_count: number;
  provider_id: string | null;
  provider_name: string | null;
  provider_practice: string | null;
  lifecycle_status: string | null;
  latest_claim_status: string | null;
};

const PAGE_SIZE = 25;

function AdminUsersPage() {
  const { user, loading, primaryRole } = useAuth();
  const navigate = useNavigate();
  const searchUsers = useServerFn(adminSearchUsersFn);
  const revokeSessions = useServerFn(adminRevokeUserSessionsFn);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || primaryRole !== "admin") navigate({ to: "/login" });
  }, [loading, user, primaryRole, navigate]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const load = async (next = offset) => {
    setIsLoading(true);
    try {
      const result = await searchUsers({ data: { query: debouncedQuery, offset: next, limit: PAGE_SIZE } });
      setUsers((result.users ?? []) as AdminUser[]);
      setNextOffset(result.nextOffset);
      setOffset(next);
    } catch (error: any) {
      toast.error(error?.message ?? "Unable to load users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (primaryRole === "admin") void load(0);
  // load deliberately runs only when the query changes, not on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryRole, debouncedQuery]);

  const handleRevokeSessions = async (account: AdminUser) => {
    if (!confirm(`Revoke all active sessions for ${account.email}? They can sign in again normally.`)) return;
    setBusyUserId(account.id);
    try {
      await revokeSessions({ data: { userId: account.id } });
      toast.success("Active sessions revoked");
    } catch (error: any) {
      toast.error(error?.message ?? "Could not revoke sessions");
    } finally {
      setBusyUserId(null);
    }
  };

  if (loading || !user || primaryRole !== "admin") return null;

  return (
    <AdminPageShell
      title="User management"
      description="Account access is separate from each provider listing’s public status."
      showFilter={false}
      showSettings={false}
      searchValue={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search name or email"
      primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: () => void load() }}
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <section className="border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div>
              <h2 className="text-sm font-semibold">Accounts</h2>
              <p className="text-xs text-muted-foreground">Searchable account directory with role and ownership context.</p>
            </div>
            {isLoading && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <div className="divide-y divide-border">
            {users.map((account) => (
              <article key={account.id} className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_220px]">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary"><UserRound className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{account.name || "Unnamed account"}</p>
                      <p className="truncate text-xs text-muted-foreground">{account.email}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {account.roles.map((role) => <span key={role} className="border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{role}</span>)}
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold ${account.email_verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {account.email_verified ? "Verified email" : "Unverified email"}
                    </span>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">Joined {new Date(account.created_at).toLocaleDateString()} {account.latest_claim_status ? `· Latest claim: ${account.latest_claim_status}` : ""}</p>
                </div>
                <div className="border-l border-border pl-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Linked listings</p>
                  {account.provider_id ? (
                    <Link to="/dashboard/admin/listings/all" search={{}} className="mt-1 block text-xs font-medium text-primary hover:underline">
                      {account.provider_practice || account.provider_name || "Provider listing"}
                    </Link>
                  ) : <p className="mt-1 text-xs text-muted-foreground">No claimed listing</p>}
                  <p className="mt-1 text-xs text-muted-foreground">{account.provider_count} total · {account.lifecycle_status ?? "n/a"}</p>
                </div>
                <div className="md:col-span-2 flex items-center justify-between border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground"><ShieldCheck className="mr-1 inline h-3.5 w-3.5" />Account controls do not change listing visibility.</p>
                  <Button size="sm" variant="outline" disabled={busyUserId === account.id} onClick={() => void handleRevokeSessions(account)}>
                    <LogOut className="mr-1.5 h-3.5 w-3.5" />Revoke sessions
                  </Button>
                </div>
              </article>
            ))}
            {!isLoading && users.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No accounts match this search.</p>}
          </div>
          <div className="flex items-center justify-between border-t border-border px-5 py-3">
            <Button size="sm" variant="outline" disabled={offset === 0 || isLoading} onClick={() => void load(Math.max(0, offset - PAGE_SIZE))}><ChevronLeft className="mr-1 h-3.5 w-3.5" />Previous</Button>
            <span className="text-xs text-muted-foreground">{offset + 1}–{offset + users.length}</span>
            <Button size="sm" variant="outline" disabled={!nextOffset || isLoading} onClick={() => nextOffset !== null && void load(nextOffset)}>Next<ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
          </div>
        </section>

        <aside className="h-fit border border-border bg-card p-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Account controls</p>
          <h2 className="mt-2 text-base font-semibold">Safe access operations</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">Revoke sessions when you suspect account misuse. This does not suspend an account, alter roles, archive a listing, or remove data.</p>
          <div className="mt-5 border-t border-border pt-4">
            <p className="flex items-center gap-2 text-xs font-medium"><Building2 className="h-4 w-4 text-primary" />Listing controls stay on the provider profile</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">Use All Listings to review a provider photo, request a replacement, or change public listing visibility.</p>
          </div>
        </aside>
      </div>
    </AdminPageShell>
  );
}
